import React, { useState } from 'react';

// AI Immigration Expert - Step 8: Final Review & Packet Generation
export const AIExpertStep8FinalPackage = ({ formData, setFormData, onNext, onBack, onClose }) => {
  const [generating, setGenerating] = useState(false);
  const [finalPackage, setFinalPackage] = useState(formData.finalPackage || null);
  const [downloading, setDownloading] = useState(false);
  const [showPostDownload, setShowPostDownload] = useState(false);

  const generateFinalPackage = async () => {
    setGenerating(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/ai/generate-package`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          case_type: 'AI Immigration Expert Analysis Package',
          petitioner_details: {
            fullName: formData.applicantName || "Immigration Applicant",
            caseType: formData.caseType || "General Immigration",
            documentsAnalyzed: (formData.uploadedFiles || []).length,
            additionalDocuments: (formData.additionalUploads || []).length
          },
          beneficiary_details: {
            fullName: formData.applicantName || "Immigration Applicant",
            status: "immigration-applicant",
            country: "United States",
            location: "United States"
          },
          relationship_details: {
            analysisType: "comprehensive-case-analysis",
            evidenceStrength: formData.finalScores?.evidenceStrength || 75,
            riskLevel: formData.finalScores?.riskLevel || "Medium",
            complianceScore: formData.finalScores?.complianceScore || 80,
            overallReadiness: formData.finalScores?.overallReadiness || 75,
            documentsLabeled: (formData.documentLabels || []).length,
            recommendationsSelected: (formData.selectedActions || []).length
          },
          documents: [
            ...(formData.uploadedFiles || []).map(doc => {
              // Create a copy without the file object for JSON serialization
              const { file, ...docWithoutFile } = doc;
              return docWithoutFile;
            }),
            ...(formData.additionalUploads || []).map(doc => {
              // Create a copy without the file object for JSON serialization
              const { file, ...docWithoutFile } = doc;
              return docWithoutFile;
            })
          ],
          analysis_results: {
            evidence_analysis: formData.analysisResults || {},
            risk_assessment: formData.riskResults || {},
            final_scores: formData.finalScores || {},
            recommendations: formData.recommendations || [],
            qa_responses: formData.qaAnswers || {},
            document_organization: formData.documentLabels || []
          },
          final_score: formData.finalScores?.overallReadiness || 75
        })
      });

      if (response.ok) {
        const result = await response.json();
        setFinalPackage(result);
        setFormData({ ...formData, finalPackage: result });
      } else {
        console.error('Package generation failed:', response.status);
        // Fallback to local package generation
        const fallbackPackage = generateFallbackPackage(formData);
        setFinalPackage(fallbackPackage);
        setFormData({ ...formData, finalPackage: fallbackPackage });
      }
    } catch (error) {
      console.error('Package generation error:', error);
      // Fallback to local package generation
      const fallbackPackage = generateFallbackPackage(formData);
      setFinalPackage(fallbackPackage);
      setFormData({ ...formData, finalPackage: fallbackPackage });
    }
    
    setGenerating(false);
  };

  const generateFallbackPackage = (data) => {
    const packageId = `AI-EXPERT-${Date.now()}`;
    const applicantName = data.applicantName || "Immigration Applicant";
    const totalDocs = (data.uploadedFiles || []).length + (data.additionalUploads || []).length;
    const overallScore = data.finalScores?.overallReadiness || 75;
    
    return {
      package_id: packageId,
      package_type: "AI Immigration Expert Analysis Package",
      applicant_name: applicantName,
      generation_date: new Date().toISOString(),
      total_documents: totalDocs + 8, // Include generated documents
      estimated_review_time: "60-90 minutes",
      package_contents: [
        'Executive_Summary_Report.docx',
        'Evidence_Strength_Analysis.docx',
        'Risk_Assessment_Report.docx',
        'Document_Organization_Guide.docx',
        'Cover_Letter_Template.docx',
        'Affidavit_Templates.docx',
        'Exhibit_List_with_Labels.docx',
        'Improvement_Recommendations.docx'
      ],
      analysis_summary: {
        overall_readiness: `${overallScore}%`,
        evidence_strength: data.finalScores?.evidenceStrength || 75,
        risk_level: data.finalScores?.riskLevel || "Medium",
        compliance_score: data.finalScores?.complianceScore || 80,
        documents_analyzed: totalDocs,
        recommendations_provided: (data.recommendations || []).length
      },
      special_instructions: [
        `Case readiness assessment: ${overallScore >= 85 ? 'Ready for submission' : overallScore >= 70 ? 'Minor improvements needed' : 'Major improvements recommended'}`,
        `Evidence strength: ${data.finalScores?.evidenceStrength || 75}% - ${data.finalScores?.evidenceStrength >= 80 ? 'Strong' : 'Needs strengthening'}`,
        `Risk assessment: ${data.finalScores?.riskLevel || 'Medium'} risk level identified`,
        `${(data.documentLabels || []).length} documents professionally labeled and organized`,
        `${(data.selectedActions || []).length} improvement actions selected for implementation`
      ]
    };
  };

  const downloadPackage = async () => {
    if (!finalPackage) return;
    
    // Check if this is a fallback package (local generation)
    if (finalPackage.package_id.startsWith('AI-EXPERT-')) {
      alert('This package was generated locally. Please regenerate the package to create a downloadable version.');
      return;
    }
    
    setDownloading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const response = await fetch(`${backendUrl}/api/download-package/${finalPackage.package_id}`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `AI_Immigration_Expert_Package_${finalPackage.package_id}.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        setShowPostDownload(true);
      } else {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        if (response.status === 404) {
          alert('Package not found. Please regenerate the package and try again.');
        } else {
          alert('Download failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      alert('Download failed. Please check your connection and try again.');
    }
    setDownloading(false);
  };

  React.useEffect(() => {
    if (!finalPackage) {
      generateFinalPackage();
    }
  }, []);

  const getReadinessColor = (score) => {
    if (score >= 85) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getReadinessMessage = (score) => {
    if (score >= 85) return 'Excellent! Your case appears ready for submission.';
    if (score >= 70) return 'Good progress. Consider addressing minor improvements.';
    return 'Significant improvements recommended before submission.';
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-4">📦 Final Review & Packet Generation</h3>
        <p className="text-gray-400 text-lg">Complete analysis package with all recommendations and organized evidence</p>
      </div>

      {generating ? (
        <div className="bg-gray-800 p-8 rounded-lg text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
          <h4 className="text-xl text-white mb-2">Generating Your Comprehensive Package</h4>
          <p className="text-gray-400">Creating analysis reports, templates, and organized evidence bundle...</p>
        </div>
      ) : finalPackage && (
        <>
          {/* Package Summary */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📋 Package Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-400">{finalPackage.total_documents}</div>
                <p className="text-gray-400">Total Documents</p>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${getReadinessColor(finalPackage.analysis_summary?.overall_readiness || 75)}`}>
                  {finalPackage.analysis_summary?.overall_readiness || 75}%
                </div>
                <p className="text-gray-400">Case Readiness</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-400">{finalPackage.analysis_summary?.documents_analyzed || 0}</div>
                <p className="text-gray-400">Documents Analyzed</p>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-400">{finalPackage.analysis_summary?.recommendations_provided || 0}</div>
                <p className="text-gray-400">Recommendations</p>
              </div>
            </div>
          </div>

          {/* Final Assessment */}
          <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 border border-blue-500 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">🎯 Final Assessment</h4>
            <div className="text-center mb-4">
              <div className={`text-4xl font-bold mb-2 ${getReadinessColor(finalPackage.analysis_summary?.overall_readiness || 75)}`}>
                {finalPackage.analysis_summary?.overall_readiness || 75}% Ready
              </div>
              <p className="text-gray-300">
                {getReadinessMessage(finalPackage.analysis_summary?.overall_readiness || 75)}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="text-center">
                <p className="text-gray-400">Evidence Strength</p>
                <p className={`text-lg font-bold ${getReadinessColor(finalPackage.analysis_summary?.evidence_strength || 75)}`}>
                  {finalPackage.analysis_summary?.evidence_strength || 75}%
                </p>
              </div>
              <div className="text-center">
                <p className="text-gray-400">Risk Level</p>
                <p className="text-lg font-bold text-yellow-400">{finalPackage.analysis_summary?.risk_level || 'Medium'}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400">USCIS Compliance</p>
                <p className={`text-lg font-bold ${getReadinessColor(finalPackage.analysis_summary?.compliance_score || 80)}`}>
                  {finalPackage.analysis_summary?.compliance_score || 80}%
                </p>
              </div>
            </div>
          </div>

          {/* Package Contents */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📁 Package Contents</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(finalPackage.package_contents || []).map((item, index) => (
                <div key={index} className="flex items-center bg-gray-700 p-3 rounded-lg">
                  <span className="text-blue-400 mr-3">📄</span>
                  <span className="text-white">{item.replace(/_/g, ' ').replace('.docx', '')}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Special Instructions */}
          <div className="bg-gray-800 p-6 rounded-lg">
            <h4 className="text-xl font-semibold text-white mb-4">📌 Key Findings & Instructions</h4>
            <div className="space-y-2">
              {(finalPackage.special_instructions || []).map((instruction, index) => (
                <div key={index} className="flex items-start">
                  <span className="text-blue-400 mr-2 mt-1">•</span>
                  <span className="text-gray-300">{instruction}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Download Section */}
          {!showPostDownload ? (
            <div className="text-center bg-gradient-to-r from-emerald-900/20 to-blue-900/20 border border-emerald-500 p-8 rounded-lg">
              <div className="text-6xl mb-4">📥</div>
              <h4 className="text-2xl font-bold text-white mb-4">Download Your Complete Analysis Package</h4>
              <p className="text-gray-300 mb-6">
                Get your comprehensive immigration case analysis with organized documents, 
                professional templates, and detailed recommendations.
              </p>
              <button
                onClick={downloadPackage}
                disabled={downloading}
                className={`px-12 py-6 rounded-xl text-2xl font-bold transition-all duration-300 ${
                  downloading
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-lg hover:shadow-emerald-500/25'
                }`}
              >
                {downloading ? (
                  <>
                    <span className="animate-spin inline-block mr-2">⏳</span>
                    Preparing Download...
                  </>
                ) : (
                  '📥 Download Complete Package'
                )}
              </button>
            </div>
          ) : (
            /* Post-Download Options */
            <div className="bg-green-900/20 border border-green-500 p-6 rounded-lg">
              <div className="text-center mb-6">
                <div className="text-4xl mb-2">✅</div>
                <h4 className="text-xl font-semibold text-white mb-2">Package Downloaded Successfully!</h4>
                <p className="text-gray-400">Your comprehensive immigration analysis package is ready for review.</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => setShowPostDownload(false)}
                  className="w-full px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
                >
                  🔄 Download Again
                </button>
                <button
                  onClick={onClose}
                  className="w-full px-6 py-4 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors font-semibold"
                >
                  ✅ Complete & Exit
                </button>
              </div>
              
              <div className="mt-4 p-4 bg-blue-900/20 border border-blue-500 rounded-lg">
                <p className="text-blue-400 font-medium mb-2">Next Steps:</p>
                <ul className="text-gray-300 space-y-1 text-sm">
                  <li>• Review the Executive Summary for key findings</li>
                  <li>• Implement the recommended improvements</li>
                  <li>• Use the provided templates for your application</li>
                  <li>• Follow the organized exhibit list for submission</li>
                </ul>
              </div>
            </div>
          )}
        </>
      )}

      {/* Navigation */}
      {!showPostDownload && (
        <div className="flex justify-between">
          <button
            onClick={onBack}
            className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            ← Back
          </button>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Close Expert Session
          </button>
        </div>
      )}
    </div>
  );
};